import React from 'react'
import { FontWeight } from '../types'
import { THEME_FONTS, getDisplayFonts, getBodyFonts, FONT_WEIGHT_LABELS, isWeightAvailable } from '../data/fonts'

interface FontPickerProps {
  label: string
  value: string
  weight: FontWeight
  onFontChange: (font: string) => void
  onWeightChange: (weight: FontWeight) => void
  type: 'display' | 'body'
}

export function FontPicker({ label, value, weight, onFontChange, onWeightChange, type }: FontPickerProps) {
  const fonts = type === 'display' ? getDisplayFonts() : getBodyFonts()

  // Get available weights for the selected font
  const selectedFont = THEME_FONTS.find(f => f.family === value)
  const availableWeights = selectedFont?.weights || ['normal']

  // Group fonts by category
  const groupedFonts = fonts.reduce((acc, font) => {
    if (!acc[font.category]) {
      acc[font.category] = []
    }
    acc[font.category].push(font)
    return acc
  }, {} as Record<string, typeof fonts>)

  const categoryLabels: Record<string, string> = {
    'sans-serif': 'Sans Serif',
    'serif': 'Serif',
    'display': 'Display',
    'handwriting': 'Script',
    'monospace': 'Monospace',
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">{label}</label>

      {/* Font family select */}
      <select
        value={value}
        onChange={(e) => {
          onFontChange(e.target.value)
          // Reset weight if not available for new font
          if (!isWeightAvailable(e.target.value, weight)) {
            onWeightChange('normal')
          }
        }}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white cursor-pointer"
        style={{ fontFamily: value }}
      >
        {Object.entries(groupedFonts).map(([category, categoryFonts]) => (
          <optgroup key={category} label={categoryLabels[category] || category}>
            {categoryFonts.map((font) => (
              <option key={font.family} value={font.family} style={{ fontFamily: font.family }}>
                {font.family}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {/* Weight select */}
      <select
        value={weight}
        onChange={(e) => onWeightChange(e.target.value as FontWeight)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white cursor-pointer"
      >
        {availableWeights.map((w) => (
          <option key={w} value={w}>
            {FONT_WEIGHT_LABELS[w]}
          </option>
        ))}
      </select>
    </div>
  )
}
